import * as React from 'react';

import { enumUnit } from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/trade/i18n/useTranslations';
import { MaterialSymbol } from '@rematter/ui-web';
import { useSetAtom } from 'jotai';
import { LucideTag } from 'lucide-react';

import { $openMaterialsModal } from '../../../../_state/loadStaged.state';
import { LoadStagedFieldProps } from '../_types';

import { useInventoryGroupStyle } from '~/modules/trade/hooks/useInventoryGroupStyle';
import { createDefaultWeights } from '~/modules/trade/utils/weight';
import {
  SelectableInventoryTags,
  SelectInventoryTagTouch,
} from '~/shared/components/select-fields/SelectInventoryTagsTouch';

export const InventoryTagItemSelectField: React.FC<LoadStagedFieldProps> = ({
  lineItem,
  inventoryItem,
  disabled,
  touchModeEnabled,
  onUpdateItem,
  onFocus,
  onBlur,
}) => {
  const t = useTranslations();
  const openMaterialsModal = useSetAtom($openMaterialsModal);

  const groupStyle = useInventoryGroupStyle({});

  const handleItemChange = React.useCallback(
    (tagId?: string, tagData?: SelectableInventoryTags) => {
      onUpdateItem(lineItem.id, {
        tagId,
        itemId: tagData?.metadata.item.id ?? undefined,
        weights: createDefaultWeights(enumUnit.Pounds, {
          gross: tagData?.metadata.grossQuantity ?? 0,
          tare: tagData?.metadata?.tare ?? 0,
          net: tagData?.metadata?.netQuantity ?? 0,
        }),
      });
    },
    [lineItem.id, onUpdateItem],
  );

  const handleClickTouchMode = React.useCallback(() => {
    openMaterialsModal({
      mode: 'change',
      itemType: 'tags',
      selectedItemId: lineItem.tagId ?? null,
      lineItemId: lineItem.id,
    });
  }, [openMaterialsModal, lineItem.tagId, lineItem.id]);

  const materialSymbol = (
    <MaterialSymbol
      symbolIcon={<LucideTag size={16} />}
      description={inventoryItem?.mnemonic ?? undefined}
      style={groupStyle}
      className="h-[44px] w-[35px] max-w-[35px]"
    />
  );

  return (
    <div className="@InventoryTagItemSelectField flex items-center gap-2">
      {materialSymbol}
      <SelectInventoryTagTouch
        value={lineItem.tagId ?? undefined}
        onChange={handleItemChange}
        placeholder={t.loadStaged.placeholders.taggedMaterial}
        onFocus={onFocus}
        onBlur={onBlur}
        isDisabled={disabled}
        variant="outlined"
        onTriggerClick={touchModeEnabled ? handleClickTouchMode : undefined}
      />
    </div>
  );
};
